import { GENERAL, LOOT } from "./Config";

/**
 * The economy of a battle: what a score is worth, what moves it between hulls, and how
 * much treasure floats in the sea. Imported by both the client (to draw it) and the server
 * (which owns it) — see design/Battle.md.
 *
 * Scores are whole numbers. A fraction of a point is not something a player can act on,
 * and the leaderboard would only round it away.
 *
 * The constants marked *tunable* come from `config/general.json` by way of
 * [Config.ts](Config.ts) and are edited in the dashboard — see design/Settings.md.
 */

/**
 * What every ship joins with. **Tunable.** Not zero, because a fresh hull has to be worth
 * shooting at — a worthless newcomer gets ignored rather than fought.
 */
export const STARTING_SCORE = GENERAL.startingScore;

/**
 * Fraction of the victim's score a single hit transfers to the attacker, for a ball with **no
 * gun behind it**.
 *
 * A percentage rather than a flat amount, so the leader is always the richest target and the gap
 * between first and last never becomes unbridgeable. That part has not changed and is the whole
 * shape of the economy (design/Battle.md).
 *
 * **What changed is where it comes from.** It used to be one global number for the whole game;
 * what a hit takes is now a property of the **cannon type** that fired it, growing on that type's
 * own per-level curve (`CannonStats.steal`, authored in the dashboard's Boat panel). It has to
 * live on the gun because the gun is the thing the shop sells, and the thing a shop sells has to
 * be the thing whose number moves — a card that advertised a steal the game did not use would be
 * advertising nothing.
 *
 * This constant survives as the **default**: what `plunderFrom` takes when the caller has no gun
 * to ask, which is a test, a tool, or a catalogue with no guns in it. It is deliberately the
 * number the whole arena was balanced around, so a caller that does not know about guns behaves
 * exactly as the game did before they had opinions.
 */
export const HIT_STEAL_FRACTION = 0.4;

/** Floor on a single hit, so a nearly-empty hull is still worth a shot. */
export const MIN_PLUNDER = 1;

/**
 * The death line. **Tunable.** A hull holding less than this is sunk outright by the next ball
 * that finds it, and its whole hold goes to whoever fired — see design/Battle.md.
 *
 * It is what turns the percentage steal into a kill. On its own, `HIT_STEAL_FRACTION` is an
 * asymptote: 40% of what is left is never all of what is left, so a chase that is going well
 * still never ends, and the pitch's "sink or get sunk in under a minute" has no last shot in it.
 * The line is where the arena stops taking a share and takes the ship.
 *
 * Zero switches death off, which is the arena as it was before the dial existed: nothing is ever
 * below zero, so nothing is ever mortal and a hit only costs points.
 */
export const DEATH_SCORE = GENERAL.deathScore;

/**
 * How long a sunk captain stays at sea before the scene changes, in seconds. **Tunable.**
 *
 * A sinking used to arrive without a moment: the ball landed, the hull left `state.ships`, and the
 * next patch put the player on the main menu. The linger is the moment — the hull is gone from the
 * arena the instant it is taken, exactly as before, and what stays behind is this client watching
 * the sea it died in: its own wreck settling, whoever sank it sailing on, the balls still in the
 * air. See design/Battle.md.
 *
 * **The client's alone.** The arena has already torn the ship down and is holding nothing but the
 * seat, so this changes nothing about the simulation and two captains lingering for different
 * counts is not a desync. It is on the General panel because it is the other half of the death
 * rule, not because the server reads it.
 *
 * Zero is the instant cut the game shipped with.
 */
export const DEATH_LINGER_SECONDS = GENERAL.deathLingerSeconds;

/**
 * Doubloons a second at the top of the board, how steeply that thins with rank, and what every
 * living ship earns regardless. **Tunable**, on the General panel — see design/Shop.md.
 */
export const PURSE_TOP_RATE = GENERAL.purseTopRate;
export const PURSE_FALLOFF = GENERAL.purseFalloff;
export const PURSE_FLOOR_RATE = GENERAL.purseFloorRate;

/**
 * How often the arena ranks the sea and pays it out, in seconds.
 *
 * **Once a second, not once a step.** A rank is not a thing that needs a frame's worth of
 * precision, and sorting fifty ships sixty times a second to answer it would be sixty times the
 * work for the same answer. It is the same argument the leaderboard's own 5 Hz refresh makes.
 */
export const PURSE_TICK_SECONDS = 1;

/**
 * Doubloons a second for holding `rank`, counting from 1.
 *
 * ```
 * rate(rank) = floor + top / rank ^ falloff
 * ```
 *
 * Rank rather than score, and a rate rather than a total, because both halves answer the same
 * question: **how well were you doing, against the people who were actually there?** A payout on
 * final score would pay the same for a good run in an empty sea as for a hard-won fifth place in a
 * full one, and nothing at all for the twenty minutes a captain spent holding second before being
 * sunk. See design/Shop.md.
 *
 * It reads the loaded constants; `purseRate` in [Settings.ts](Settings.ts) is the same rule over a
 * settings object, which is what the dashboard prices unsaved sliders with. They are two callers
 * of one formula rather than two formulas — and if that ever stops being obvious, this is the copy
 * the arena pays on.
 */
export function purseFor(rank: number): number {
    return PURSE_FLOOR_RATE + PURSE_TOP_RATE / Math.pow(Math.max(1, Math.floor(rank)), PURSE_FALLOFF);
}

/**
 * Whether a hull with this score is sunk by the next ball that finds it, rather than merely
 * plundered. One function for a rule both ends read, the way `canTurbo` is: the server to sink
 * the ship, the client to warn the captain that the next hit is the last one.
 */
export function isMortal(score: number): boolean {
    return score < DEATH_SCORE;
}

/**
 * How long an abandoned hull keeps sailing before it founders, in seconds.
 *
 * A captain who drops out leaves a **ghost ship** rather than a hole in the sea: the hull holds
 * its course, does not steer, does not shoot and does not collect, and is worth exactly the
 * plunder it was worth a second earlier (design/Battle.md). Removing it instead would make
 * closing a tab the cheapest way to protect a lead, and would delete a target somebody was in
 * the middle of earning.
 *
 * It founders eventually because nothing else takes it out of the sea: a ghost gains no score,
 * so nobody has to hunt it, and a room that runs all evening would otherwise silt up with
 * derelicts nobody bothered to sink. Two minutes is long enough that a fight already in progress
 * finishes on the wreck, and short enough that the sea is never mostly ghosts.
 *
 * A decision, not a dial: it is about how long the *arena* holds a seat open, not about how the
 * economy is priced.
 */
export const GHOST_LIFETIME = 120;

/**
 * How long the arena holds a captain's seat open after their socket drops, in seconds, **before**
 * the hull becomes a ghost.
 *
 * A blip is not a departure. Wifi stutters, a laptop sleeps for a moment, a phone changes cell —
 * and until this existed every one of those cost the ship for good and handed it to whoever was
 * nearest, which is a harsh price for something that was never the player's doing. Inside the
 * window the hull simply sails on: it holds its course, it is still a normal target, and if the
 * socket comes back the captain has it again with their plunder intact.
 *
 * **Five seconds, and the number is a balance between two unfairnesses.** Too short and a real
 * blip still costs the ship. Too long and a hull that nobody is sailing sits in a firefight
 * unpunished — worse, a captain about to be sunk could pull their own cable and come back to a
 * ship somebody had spent ten seconds failing to kill. Five is about the longest a network hiccup
 * lasts and about the shortest a fight takes to resolve.
 *
 * It is **in front of** the ghost rather than instead of it: past the window the hull is abandoned
 * exactly as it always was and founders on GHOST_LIFETIME. A decision, not a dial, for the same
 * reason GHOST_LIFETIME is one — it is about how long a seat is held, not about how the economy is
 * priced. See design/Battle.md.
 */
export const RECONNECT_WINDOW = 5;

/**
 * Points in one floating loot item. **Tunable.** A rounding error to the leader, a raise to
 * a beginner.
 */
export const LOOT_VALUE = LOOT.lootValue;

/**
 * How many loot items the sea holds at once. **Tunable.** Constant during a battle: one is
 * respawned elsewhere for every one taken, so a region cannot be stripped bare.
 */
export const LOOT_COUNT = LOOT.lootCount;

/**
 * How many patches the standing stock is gathered into. **Tunable.** Zero is an even scatter
 * over the whole sea, which is what the arena did before this was a dial; above zero the
 * room mints this many centres when it is created and every item spawns near one of them.
 *
 * The centres last as long as the room does. A patch that moved every time somebody emptied
 * it would be indistinguishable from an even scatter — the point of a patch is that it is a
 * *place*, which players learn, sail to, and meet each other at.
 */
export const LOOT_CLUSTERS = LOOT.lootClusters;

/**
 * How far from its centre a patched item can land, in world pixels. **Tunable.** Items are
 * spread evenly over the disc rather than around the centre, so a patch is a field of coins
 * and not a bullseye.
 */
export const LOOT_CLUSTER_RADIUS = LOOT.lootClusterRadius;

/**
 * How close a hull's centre has to come to loot to take it, **as a fraction of that ship's own
 * length**. It is the same 0.4 the hit radius uses, and that is not a coincidence any more — it
 * is the same circle.
 *
 * **A coin is collected exactly when it touches the hull**, by the circle a cannonball has to
 * enter to hit it. That was already true in spirit: design/Battle.md called the pickup
 * "deliberately the same 0.4 the pickup uses", and while every ship was 190px long the
 * two were the same 75 pixels. They came apart the moment captains started growing — a boat at
 * the top of the scale curve is 540px long and was still swallowing coins at 75px, which is a
 * quarter of the way from its centre to its stern. Coins slid under the deck and vanished
 * somewhere near the mainmast.
 *
 * So it scales, and what it costs is almost nothing: the magnet decides who gets a coin, and it
 * does **not** scale (see below). All this changes is where the last few pixels are closed.
 *
 * Use `pickupRange(size)` rather than multiplying by hand — the arena squares it once per step.
 */
export const LOOT_PICKUP_FACTOR = 0.4;

/** How close this ship's centre has to come to a coin to take it, in world pixels. */
export function pickupRange(hullLength: number): number {
    return hullLength * LOOT_PICKUP_FACTOR;
}

/**
 * How far a hull reaches for a coin, from its centre, in world pixels. **Tunable**, and the
 * default is three lengths of the free boat (BOAT_LENGTH 190, design/Ships.md) — far enough that
 * a coin a boat was never going to touch still comes to it, close enough that the pull is a thing
 * that happens *near* the ship rather than a vacuum over the whole screen.
 *
 * **It is the same distance for everybody, and does not grow with the ship.** That is the one
 * place this document deliberately breaks the "measured in hull lengths" habit, and the reason is
 * design/Battle.md's: loot exists for the **small** player. A magnet that scaled would hand the
 * biggest hull in the sea a reach of nine of its own lengths and turn the thing that is supposed
 * to be a floor under a bad run into a reward for having had a good one. A fully grown captain
 * sweeps exactly as much sea as a fresh one; what they have instead is a wider hull to sweep it
 * with, which is `pickupRange` above and is worth about a fifth of a coin.
 *
 * Zero switches the magnet off entirely: coins sit still and are taken by sailing over them,
 * which is the arena as it was before this dial existed.
 */
export const LOOT_MAGNET_RANGE = LOOT.lootMagnetRadius;

/**
 * How fast a coin drifts at the very edge of the magnet, in pixels per second. Deliberately
 * slow: at the rim the pull should read as the coin *noticing* the hull, not as being fired
 * at it, and a boat that turns away should be able to leave it behind.
 */
export const LOOT_MAGNET_SPEED_FAR = 40;

/**
 * How fast a coin is travelling as it reaches the hull, in pixels per second. Comfortably
 * above the fastest a ship can sail, or a coin overtaken from behind would never close the
 * gap and would trail the boat at arm's length for as long as it kept its course.
 */
export const LOOT_MAGNET_SPEED_NEAR = 900;

/**
 * How sharply the speed climbs between the two. Above 1, so most of the acceleration happens
 * in the last part of the trip: the coin creeps, then snaps in. Exactly the shape of the
 * feedback the effect exists for — the pickup should land as an event, not as a long tow.
 */
const LOOT_MAGNET_CURVE = 2.5;

/**
 * How fast a coin `distance` pixels from a hull's centre is dragged toward it, in pixels per
 * second. Zero outside the magnet.
 *
 * A function of distance alone, with no velocity kept anywhere: a coin's speed is decided
 * fresh each step from where it is. That is what lets the magnet exist without adding a field
 * to the `Loot` schema (design/Networking.md) or any per-item state to the room, and it means
 * a coin handed off between two hulls never carries momentum from the first one.
 */
export function magnetSpeed(distance: number): number {
    if (distance >= LOOT_MAGNET_RANGE) return 0;

    // 0 at the rim, 1 at the hull's centre.
    const closeness = 1 - distance / LOOT_MAGNET_RANGE;
    return LOOT_MAGNET_SPEED_FAR + (LOOT_MAGNET_SPEED_NEAR - LOOT_MAGNET_SPEED_FAR) * Math.pow(closeness, LOOT_MAGNET_CURVE);
}

/**
 * Points burned per second of turbo. **Tunable.** At the default of one coin's worth a
 * second, a loot pickup buys exactly one second of speed, which is the exchange rate the
 * whole control is built around — see design/Battle.md. Nothing is destroyed, it is
 * spilled: see TURBO_SPILL_SECONDS.
 */
export const TURBO_COST_PER_SECOND = GENERAL.turboCost;

/**
 * Turbo cuts out at or below this score. **Tunable.** A player can burn most of a hull's
 * worth chasing a kill, but never the last of it — there is always a ship left to fight
 * with, and a score of zero would make MIN_PLUNDER meaningless.
 */
export const TURBO_MIN_SCORE = GENERAL.minimumBoatScore;

/**
 * Seconds of turbo that buy one expelled coin. Falls out of the two constants above rather
 * than being picked: the charge is taken in whole LOOT_VALUE chunks so that every point
 * leaving a hull lands in the water as a real, collectable coin. A smooth drain with chunky
 * spill would mint or destroy the difference.
 *
 * `Infinity` when TURBO_COST_PER_SECOND is tuned to zero, which is the right answer: no
 * amount of burning ever reaches a chunk, so a free turbo spills nothing.
 */
export const TURBO_SPILL_SECONDS = LOOT_VALUE / TURBO_COST_PER_SECOND;

/**
 * How far astern of the hull's centre a spilled coin lands, **as a fraction of that ship's own
 * length** — comfortably past the pickup circle, or a boat under turbo would swallow its own
 * spill and travel for free.
 *
 * **It scales with the ship, and it has to.** "Astern" is a place on a boat, not a distance in
 * the sea: a flat 130 pixels is behind the stern of the free boat and somewhere near the
 * mainmast of a fully grown one, so a big captain's spill came out of the middle of their own
 * deck. Because the pickup circle scales too, the safety property this constant exists for
 * survives by construction — 0.7 of a length is always comfortably past 0.4 of one, whatever the
 * ship.
 *
 * It is **not** kept past LOOT_MAGNET_RANGE, which is wider and is a dial besides. The
 * magnet instead holds off on a coin until the hull that burned it has sailed further away
 * than the magnet reaches — the room tracks which spill is still unarmed — so a burning ship
 * cannot tow its own trail at any radius. Once it has genuinely left a coin behind, coming
 * back for it is a course change, and the magnet helps with it like it helps with anything.
 */
export const TURBO_SPILL_ASTERN_FACTOR = 0.7;

/** How far astern of this ship's centre its spilled coins land, in world pixels. */
export function spillAstern(hullLength: number): number {
    return hullLength * TURBO_SPILL_ASTERN_FACTOR;
}

/**
 * Sideways scatter on a spilled coin, as a fraction of the ship's own length, so a burn lays a
 * ragged trail rather than a dotted line. It scales for the same reason the drop astern does: a
 * flat 28px is a ragged trail behind the free boat and a straight line behind a grown one.
 */
export const TURBO_SPILL_SPREAD_FACTOR = 28 / 190;

/** How far to either side of the wake a spilled coin may land, in world pixels. */
export function spillSpread(hullLength: number): number {
    return hullLength * TURBO_SPILL_SPREAD_FACTOR;
}

/**
 * How long a spilled coin floats before it sinks, in seconds. Long enough that a pursuer is
 * paid for the chase, short enough that a burned wake is a moment rather than litter — the
 * sea's density is a designed constant (LOOT_COUNT) and spill is the one thing that adds to it.
 */
export const TURBO_SPILL_LIFETIME = 30;

/**
 * Whether a hull with this score may light its turbo. One function for a rule both sides
 * apply: the server to charge for it, the client to predict it. They disagree by one patch
 * about the score, which the usual reconciliation absorbs.
 */
export function canTurbo(score: number): boolean {
    return score > TURBO_MIN_SCORE;
}

/**
 * What a hit takes off a victim's score, in whole points. Never more than they have, never
 * less than MIN_PLUNDER. Nothing is destroyed in the transfer — the attacker gains exactly
 * this. Called by the arena when a cannonball finds a hull; see design/Battle.md.
 *
 * **A mortal hull pays everything.** Below DEATH_SCORE the ball does not take a share, it takes
 * the ship: the whole hold moves and the hull leaves the sea. That is one rule rather than two —
 * the arena asks `isMortal` about the same score to know whether to remove the ship, so the
 * payout and the sinking can never disagree about how much was aboard.
 */
export function plunderFrom(score: number, steal: number = HIT_STEAL_FRACTION): number {
    if (isMortal(score)) return score;

    // Clamped rather than trusted: `steal` comes off a catalogue a designer edits, and a share
    // above 1 would hand the attacker more than the victim is holding — points minted out of
    // nothing, which is the one thing this whole economy promises never happens.
    const share = Number.isFinite(steal) ? Math.min(1, Math.max(0, steal)) : HIT_STEAL_FRACTION;

    return Math.min(score, Math.max(MIN_PLUNDER, Math.floor(score * share)));
}
